import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { dashboardApi } from '@/services/dashboardApi';
import { useLoad, Loading, Table, Badge, Search, Kpi, Block, eur, money, fdate, todayLocal, btnGold, btnGhost } from './ui';
import { useConfirm } from '../ConfirmDialog';

const LEVEL = { n1: 'N1', n2: 'N2', n3: 'N3', ref_pro: 'Ref. PRO' };
const FILTERS = { pendientes: 'Pendientes de pago', exigibles: 'Exigibles sin pagar', no_exigibles: 'Aún no exigibles', pagadas: 'Pagadas', todas: 'Todas' };

export default function CdmComisiones({ cdm }) {
  const days = cdm?.rules?.commission_payable_days ?? 15;
  const [dialog, ask] = useConfirm();
  const { data, error, reload } = useLoad(() => dashboardApi.cdmCommissions(), []);
  const [view, setView] = useState('beneficiarios');
  const [filter, setFilter] = useState('exigibles');
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const lines = useMemo(() => (data?.lines || []).filter((l) => {
    if (filter === 'pendientes' && l.paid) return false;
    if (filter === 'exigibles' && (l.paid || !l.payable)) return false;
    if (filter === 'no_exigibles' && (l.paid || l.payable)) return false;
    if (filter === 'pagadas' && !l.paid) return false;
    if (!q) return true;
    return [l.sale_id, l.beneficiary_id, l.beneficiary_name, l.client].some((v) => (v || '').toLowerCase().includes(q.toLowerCase()));
  }), [data, filter, q]);
  if (!data) return <Loading error={error} />;

  const toggle = (k) => setSel((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const mark = async (paid) => {
    const keys = [...sel];
    if (!keys.length) return;
    const chosen = data.lines.filter((l) => sel.has(l.key));
    // Totales por divisa (se paga en la divisa en que pagó el socio: nunca se convierte)
    const byCur = {};
    chosen.forEach((l) => { const c = String(l.currency || 'EUR').toUpperCase(); byCur[c] = (byCur[c] || 0) + (Number(l.amount) || 0); });
    const totals = Object.entries(byCur).map(([c, v]) => money(v, c)).join(' + ');
    const names = [...new Set(chosen.map((l) => `${l.beneficiary_name || l.beneficiary_id} (${l.beneficiary_id})`))];
    const who = names.length <= 3 ? names.join(', ') : `${names.length} beneficiarios`;
    let force = false; let reason = null;
    const blocked = chosen.filter((l) => paid && !l.payable && !l.paid);
    if (blocked.length) {
      reason = await ask({
        title: 'Hay comisiones que aún no son exigibles',
        description: `${blocked.length} de las ${chosen.length} líneas todavía no cumplen la regla (100 % pagado, T&C firmados y ${days} días). Si ya se pagaron fuera de la regla, indica el motivo: quedará registrado.`,
        details: blocked.slice(0, 4).map((l) => `${l.sale_id} · ${l.beneficiary_id}: ${l.blocked_reasons.join(' · ')}`),
        input: { label: 'Motivo', placeholder: 'Ej.: pagada a mano el 30/09 por transferencia', required: true, multiline: true },
        confirmLabel: 'Continuar', tone: 'danger',
      });
      if (!reason) return;
      force = true;
    }
    const paidAt = await ask({
      title: paid ? 'Marcar comisiones como pagadas' : 'Desmarcar comisiones pagadas',
      details: [
        { label: 'Líneas', value: chosen.length },
        { label: 'Total', value: totals || '—' },
        { label: names.length === 1 ? 'Beneficiario' : 'Beneficiarios', value: who },
        paid ? 'Cada línea se paga en la divisa en que pagó el socio.' : 'Volverán a contar como pendientes de pago.',
        force ? `Se registra el motivo: «${reason}».` : null,
      ],
      input: paid ? { label: 'Fecha de pago', type: 'date', defaultValue: todayLocal(), required: true } : undefined,
      confirmLabel: paid ? `Marcar ${chosen.length} como pagadas` : `Desmarcar ${chosen.length}`,
      tone: paid ? 'gold' : 'danger',
    });
    if (!paidAt) return;
    setBusy(true);
    try {
      await dashboardApi.cdmMark({ keys, paid, force, reason, paid_at: paid ? paidAt : undefined });
      toast.success(paid ? `${chosen.length} comisiones marcadas como pagadas el ${fdate(paidAt)}` : `${chosen.length} comisiones desmarcadas`);
      setSel(new Set());
      reload();
    } catch (e) { toast.error(e.message || 'No se ha podido guardar'); } finally { setBusy(false); }
  };

  const t = data.totals;
  return (
    <div className="space-y-6" data-testid="cdm-comisiones">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Devengado" value={eur(t.accrued_eur)} sub={`EUR ${eur(t.eur)} · USD ${money(t.usd, 'usd')}`} />
        <Kpi label="En W2USDT" value={money(t.w2usdt, 'W2USDT')} />
        <Kpi label="Pagado" value={eur(t.paid_eur)} />
        <Kpi label="Exigible sin pagar" value={eur(t.payable_unpaid_eur)} tone={t.payable_unpaid_eur > 0 ? 'bad' : undefined} />
        <Kpi label="Aún no exigible" value={eur(t.not_yet_payable_eur)} sub={`100 % pagado + T&C + ${days} días`} />
      </div>
      <div className="flex gap-1 rounded-xl border border-white/[0.06] bg-white/[0.02] p-1 w-fit">
        {[['beneficiarios', 'Por beneficiario'], ['lineas', 'Detalle por venta']].map(([k, v]) => (
          <button key={k} onClick={() => setView(k)} className={`px-3 py-1.5 rounded-lg text-sm ${view === k ? 'bg-[#c9a84c]/15 text-[#c9a84c]' : 'text-gray-400'}`}>{v}</button>
        ))}
      </div>
      {view === 'beneficiarios' ? (
        <Block title="Resumen por beneficiario">
          <Table rowKey="beneficiary_id" rows={data.rows} cols={[
            { key: 'beneficiary_id', label: 'ID', className: 'text-white font-medium' },
            { key: 'name', label: 'Nombre' },
            { key: 'accrued_eur', label: 'Devengado', align: 'right', render: (r) => eur(r.accrued_eur) },
            { key: 'backed_eur', label: 'Con dinero real', align: 'right', render: (r) => eur(r.backed_eur) },
            { key: 'eur', label: 'Devengado EUR', align: 'right', render: (r) => (r.eur ? money(r.eur, 'eur') : '—') },
            { key: 'usd', label: 'Devengado USD', align: 'right', render: (r) => (r.usd ? money(r.usd, 'usd') : '—') },
            { key: 'w2usdt', label: 'Devengado W2USDT', align: 'right', render: (r) => (r.w2usdt ? money(r.w2usdt, 'W2USDT') : '—') },
            { key: 'paid_eur', label: 'Pagado', align: 'right', render: (r) => eur(r.paid_eur) },
            { key: 'payable_unpaid_eur', label: 'Exigible', align: 'right', render: (r) => (r.payable_unpaid_eur ? <span className="text-red-300">{eur(r.payable_unpaid_eur)}</span> : '—') },
            { key: 'not_yet_payable_eur', label: 'No exigible aún', align: 'right', render: (r) => (r.not_yet_payable_eur ? eur(r.not_yet_payable_eur) : '—') },
          ]} />
        </Block>
      ) : (
        <Block title={`Líneas (${lines.length})`} right={<Search value={q} onChange={setQ} placeholder="Buscar venta, socio o beneficiario" />}>
          <div className="flex flex-wrap items-center gap-2">
            {Object.entries(FILTERS).map(([k, v]) => <button key={k} onClick={() => { setFilter(k); setSel(new Set()); }} className={`px-3 py-1.5 rounded-full text-xs border ${filter === k ? 'border-[#c9a84c]/40 text-[#c9a84c] bg-[#c9a84c]/10' : 'border-white/10 text-gray-400'}`}>{v}</button>)}
            <span className="ml-auto text-xs text-gray-400">{sel.size} seleccionadas</span>
            <button disabled={!sel.size || busy} onClick={() => mark(true)} className={btnGold}>Marcar pagadas</button>
            <button disabled={!sel.size || busy} onClick={() => mark(false)} className={btnGhost}>Desmarcar</button>
          </div>
          <Table rowKey="key" rows={lines} cols={[
            { key: 'sel', label: '', noSort: true, render: (l) => <input type="checkbox" checked={sel.has(l.key)} onChange={() => toggle(l.key)} aria-label={`Seleccionar ${l.key}`} /> },
            { key: 'date', label: 'Fecha', render: (l) => fdate(l.date) },
            { key: 'sale_id', label: 'Venta', render: (l) => (<div className="text-white">{l.sale_id}<p className="text-xs text-gray-400">{l.origin} · {l.client}</p></div>) },
            { key: 'level', label: 'Nivel', render: (l) => LEVEL[l.level] || l.level },
            { key: 'beneficiary_id', label: 'Beneficiario', render: (l) => (<div>{l.beneficiary_id}<p className="text-xs text-gray-400">{l.beneficiary_name}</p></div>) },
            { key: 'amount', label: 'Importe', align: 'right', render: (l) => money(l.amount, l.currency) },
            { key: 'amount_eur', label: 'EUR', align: 'right', render: (l) => eur(l.amount_eur) },
            { key: 'how_paid', label: 'Cómo pagó', render: (l) => <span className="text-xs">{l.how_paid}</span> },
            { key: 'payable', label: 'Estado', render: (l) => (l.paid ? <Badge tone="good">Pagada {l.paid_at ? fdate(l.paid_at) : ''}</Badge>
              : l.payable ? <Badge tone="bad">Exigible</Badge>
                : <Badge tone="neutral" title={l.blocked_reasons.join(' · ')}>No exigible</Badge>) },
            { key: 'why', label: 'Motivo', noSort: true, render: (l) => <span className="text-xs text-gray-400">{l.paid ? '' : l.blocked_reasons.join(' · ')}</span> },
          ]} />
        </Block>
      )}
      <p className="text-xs text-gray-400">Regla (07/10/2026): una comisión es exigible {days} días después de que el socio complete el 100 % de su pago y firme los T&C. Se paga en la misma divisa en que pagó el socio.</p>
      {dialog}
    </div>
  );
}
